import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/auth/auth-guard.service';
import { AuthGuardWithForcedLogin } from './core/auth/auth-guard-with-forced-login.service';
import { ProfileGuard } from './core/guards/profile.guard';
import { ModuleAccessGuard } from './core/guards/module-access.guard';
import { FeatureFlagsGuard } from './core/guards/feature-flags.guard';
import { ComingSoonComponent } from './core/components/coming-soon/coming-soon.component';
import { permissionsResolver } from './core/resolvers/permissions.resolver';

const routes: Routes = [
  {
    path: 'coming-soon',
    component: ComingSoonComponent,
    canActivate: [AuthGuardWithForcedLogin, ProfileGuard]
  },
  {
    path: 'home',
    loadChildren: () => import('./modules/home/home.module').then(m => m.HomeModule),
    canActivate: [AuthGuardWithForcedLogin, ProfileGuard],
  },
  {
    path: 'products',
    loadChildren: () => import('./modules/products/products.module').then(m => m.ProductsModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'PRODUCTS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'providers',
    loadChildren: () => import('./modules/providers/providers.module').then(m => m.ProvidersModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'SUPPLIERS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'customers',
    loadChildren: () => import('./modules/customers/customers.module').then(m => m.CustomersModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'CUSTOMERS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'channels',
    loadChildren: () => import('./modules/channels/channels.module').then(m => m.ChannelsModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'CHANNELS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'ai-agents',
    loadChildren: () => import('./modules/ai-agents/ai-agents.routes').then(m => m.AI_AGENTS_ROUTES),
    canActivate: [AuthGuard, ProfileGuard]
  },
  {
    path: 'digitalitzations',
    loadChildren: () => import('./modules/digitalitzations/digitalitzations.module').then(m => m.DigitalitzationsModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'DIGITALIZATIONS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'translations',
    loadChildren: () => import('./modules/translations/translations.module').then(m => m.TranslationsModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'TRANSLATIONS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'media',
    loadChildren: () => import('./modules/multimedia/multimedia.module').then(m => m.MultimediaModule),
    canActivate: [AuthGuard, ProfileGuard, FeatureFlagsGuard, ModuleAccessGuard],
    data: { featureFlag: 'MEDIA', moduleCode: 'MEDIA' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'alignments',
    loadChildren: () => import('./modules/alignments/alignments.module').then(m => m.AlignmentsModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'ALIGNMENTS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'administration',
    loadChildren: () => import('./modules/administration/administration.module').then(m => m.AdministrationModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'ADMINISTRATION' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'settings',
    loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule),
    canActivate: [AuthGuard, ProfileGuard]
  },
  {
    path: 'pills',
    loadChildren: () => import('./modules/pills/pills.module').then(m => m.PillsModule),
    canActivate: [AuthGuard, ProfileGuard]
  },
  {
    path: 'homologations',
    loadChildren: () => import('./modules/homologations/homologations.module').then(m => m.HomologationsModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'HOMOLOGATIONS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'distributors',
    loadChildren: () => import('./modules/distributors/distributors.module').then(m => m.DistributorsModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'DISTRIBUTORS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'conflicts',
    loadChildren: () => import('./modules/conflicts/conflicts.module').then(m => m.ConflictsModule),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'CONFLICTS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'claims',
    loadChildren: () => import('./modules/claims/claims.routes').then(m => m.CLAIMS_ROUTES),
    canActivate: [AuthGuard, ProfileGuard, ModuleAccessGuard],
    data: { moduleCode: 'CLAIMS' },
    resolve: { permissions: permissionsResolver }
  },
  {
    path: 'inbox',
    loadChildren: () => import('./modules/notifications/notifications.module').then(m => m.NotificationsModule),
    canActivate: [AuthGuard, ProfileGuard]
  },
  { path: '', redirectTo: '/home', pathMatch: 'full' },
  { path: '**', redirectTo: '/home' }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes)
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
